import React, { useMemo } from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DECISION_CHIP_STYLES = {
  new: { label: 'N', color: '#34d399', bg: 'rgba(16, 185, 129, 0.15)' },
  extend: { label: 'E', color: '#60a5fa', bg: 'rgba(59, 130, 246, 0.15)' },
  merge: { label: 'M', color: '#c084fc', bg: 'rgba(168, 85, 247, 0.15)' },
};

function toDateKey(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function DecisionChip({ type, count }) {
  if (!count) return null;
  const style = DECISION_CHIP_STYLES[type];
  return (
    <Chip
      label={`${style.label} ${count}`}
      size="small"
      sx={{
        height: 18,
        fontSize: '0.62rem',
        fontWeight: 700,
        color: style.color,
        background: style.bg,
        border: `1px solid ${style.color}40`,
        '& .MuiChip-label': { px: 0.6 },
      }}
    />
  );
}

/**
 * Lightweight month-grid calendar (not a full date-picker). Renders 7-column
 * weeks for the given month, with small colored decision-count chips per day.
 *
 * @param {number} year
 * @param {number} month  0-indexed (0 = January)
 * @param {Object} daySummariesByDate  { 'YYYY-MM-DD': { new_count, extend_count, merge_count, total_count } }
 * @param {string|null} selectedDate  'YYYY-MM-DD'
 * @param {(dateKey: string) => void} onDayClick
 * @param {() => void} onPrevMonth
 * @param {() => void} onNextMonth
 */
function SynthesisCalendar({
  year,
  month,
  daySummariesByDate = {},
  selectedDate = null,
  onDayClick,
  onPrevMonth,
  onNextMonth,
}) {
  const monthLabel = useMemo(
    () => new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    [year, month],
  );

  const weeks = useMemo(() => {
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay(); // 0 = Sunday
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < startOffset; i++) {
      cells.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push(new Date(year, month, day));
    }
    while (cells.length % 7 !== 0) {
      cells.push(null);
    }

    const rows = [];
    for (let i = 0; i < cells.length; i += 7) {
      rows.push(cells.slice(i, i + 7));
    }
    return rows;
  }, [year, month]);

  const todayKey = toDateKey(new Date());

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#e2e8f0' }}>
          {monthLabel}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={onPrevMonth}
            sx={{ color: '#94a3b8', '&:hover': { color: '#e2e8f0', background: 'rgba(255,255,255,0.08)' } }}
          >
            <NavigateBeforeIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={onNextMonth}
            sx={{ color: '#94a3b8', '&:hover': { color: '#e2e8f0', background: 'rgba(255,255,255,0.08)' } }}
          >
            <NavigateNextIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
        {WEEKDAY_LABELS.map((label) => (
          <Typography
            key={label}
            variant="caption"
            sx={{
              color: '#64748b',
              fontWeight: 600,
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              textAlign: 'center',
              pb: 0.5,
            }}
          >
            {label}
          </Typography>
        ))}

        {weeks.map((week, weekIdx) =>
          week.map((date, dayIdx) => {
            if (!date) {
              return <Box key={`${weekIdx}-${dayIdx}`} />;
            }
            const dateKey = toDateKey(date);
            const summary = daySummariesByDate[dateKey];
            const hasDecisions = summary && summary.total_count > 0;
            const isSelected = selectedDate === dateKey;
            const isToday = dateKey === todayKey;

            return (
              <Box
                key={dateKey}
                onClick={hasDecisions ? () => onDayClick(dateKey) : undefined}
                sx={{
                  minHeight: 72,
                  borderRadius: '8px',
                  border: isSelected
                    ? '1px solid rgba(59, 130, 246, 0.6)'
                    : '1px solid rgba(255, 255, 255, 0.08)',
                  background: isSelected
                    ? 'rgba(59, 130, 246, 0.12)'
                    : hasDecisions
                    ? '#0f172a'
                    : 'transparent',
                  p: 0.75,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  cursor: hasDecisions ? 'pointer' : 'default',
                  transition: 'all 0.15s ease',
                  '&:hover': hasDecisions
                    ? { border: '1px solid rgba(59, 130, 246, 0.4)', background: 'rgba(59, 130, 246, 0.08)' }
                    : {},
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: isToday ? '#60a5fa' : '#94a3b8',
                    fontWeight: isToday ? 700 : 500,
                    fontSize: '0.75rem',
                  }}
                >
                  {date.getDate()}
                </Typography>
                {hasDecisions && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                    <DecisionChip type="new" count={summary.new_count} />
                    <DecisionChip type="extend" count={summary.extend_count} />
                    <DecisionChip type="merge" count={summary.merge_count} />
                  </Box>
                )}
              </Box>
            );
          }),
        )}
      </Box>
    </Box>
  );
}

export default SynthesisCalendar;
